"use client";

import type { FC } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIsPc } from "@/src/hooks/useIsPc";
import { useOnboardingWizardBootstrap } from "@/src/hooks/onboarding/useOnboardingWizardBootstrap";
import { useOnboardingHarnessQuery } from "@/src/hooks/onboarding/useOnboardingHarnessQuery";
import {
  buildHarnessQuizAnswers,
  buildHarnessResultValues,
} from "@/src/hooks/onboarding/onboardingHarnessQuery";
import { useOnboardingGoalScoring } from "@/src/hooks/onboarding/useOnboardingGoalScoring";
import { useOnboardingWizardApiFlow } from "@/src/hooks/onboarding/useOnboardingWizardApiFlow";
import { getLoaderChecklistForGoalType } from "@/src/hooks/onboarding/loaderChecklistByGoalType";
import { mapGoalIdToGoalType } from "@/src/hooks/onboarding/mapGoalIdToGoalType";
import { mapResultValuesToResultUI } from "@/src/hooks/onboarding/mapResultValuesToResultUI";
import {
  getOnboardingV3Session,
  patchOnboardingV3Session,
  persistOnboardingV3Session,
} from "@/src/hooks/onboarding/onboardingV3SessionStorage";
import { resolveOnboardingPlanUnlock } from "@/src/hooks/onboarding/resolveOnboardingFirstPaywallOutcome";
import type { OnboardingV3PlanPreview } from "@/src/hooks/onboarding/onboardingV3Types";
import {
  OnboardingV3ApiError,
  runOnboardingPlanPreviewRequest,
} from "@/src/hooks/onboarding/onboardingV3Api";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";
import { useRequestHeader } from "@/src/hooks/useRequestHeader";
import { useMemberUser } from "@/src/hooks/useMemberUser";
import { usePlanGoalChatOpen } from "@/src/hooks/plan/usePlanGoalChatOpen";
import { useShellRouter } from "@/src/hooks/useShellAwareRouter";
import { trackEvent } from "@/src/utils/tracking";
import log from "logbeacon";
import { useSnapshot } from "valtio";
import { rootStore } from "@/src/store";
import { callEdgenModalWhenRootReadyAsync } from "@/src/components/EdgenModal/waitForEdgenModalRoot";
import { openOnboardingPaywall } from "@/src/components/ChatOnboarding/openOnboardingPaywall";
import { openOnboardingPlanOverviewModal } from "./PlanOverviewModal/openOnboardingPlanOverviewModal";
import { PlanOverviewModalContent } from "./PlanOverviewModal/PlanOverviewModalContent";
import { navigateOnboardingPlanUnlockToBrief } from "@/src/hooks/onboarding/navigateOnboardingPlanUnlockToBrief";
import { ChoiceQuizStep } from "./steps/ChoiceQuizStep";
import { GoalSelectionStep } from "./steps/GoalSelectionStep";
import { LoaderErrorStep } from "./steps/LoaderErrorStep";
import { LoaderStep } from "./steps/LoaderStep";
import { NameQuizStep } from "./steps/NameQuizStep";
import { NumericQuizStep } from "./steps/NumericQuizStep";
import { ResultStep } from "./steps/ResultStep";
import { ScoreIntroStep } from "./steps/ScoreIntroStep";
import { FitnessScoreResultStep } from "./steps/FitnessScoreResultStep";
import { ValuePropStep } from "./steps/ValuePropStep";
import { MOCK_ONBOARDING_FITNESS_SCORE } from "./__fixtures__/onboardingFitnessScore";
import {
  getQuizDefinitionForStep,
  useOnboardingWizardStep,
} from "./useOnboardingWizardStep";
import type { OnboardingWizardStep } from "./types";

interface OnboardingWizardShellContentProps {
  initialStep: OnboardingWizardStep;
}

const OnboardingWizardShellContent: FC<OnboardingWizardShellContentProps> = ({
  initialStep,
}) => {
  const [isPC] = useIsPc();
  const harness = useOnboardingHarnessQuery();
  const harnessModalsOpenedRef = useRef(false);
  const goalCardsTrackedRef = useRef(false);
  const [isOpeningPlanOverview, setIsOpeningPlanOverview] = useState(false);
  const [pcPlanPreview, setPcPlanPreview] =
    useState<OnboardingV3PlanPreview | null>(null);
  const { chatApi } = useOspApiClientProvider();
  const { headers } = useRequestHeader();
  const { fingerprint } = useSnapshot(rootStore.user);
  const router = useShellRouter();
  const { openPlanResumeDraftGoalChat } = usePlanGoalChatOpen();
  const { data: memberPlanInfo } = useMemberUser();
  const isMember = memberPlanInfo?.isMember === true;

  const requestHeaders = useMemo(
    () => ({
      ...headers,
      ...(fingerprint ? { guestId: String(fingerprint) } : {}),
    }),
    [headers, fingerprint],
  );

  const wizard = useOnboardingWizardStep({
    initialStep,
    simulateLoadingError: harness.simulateLoadingError,
    resultVariantOverride: harness.resultVariant ?? undefined,
    initialAnswers: buildHarnessQuizAnswers(harness.quizState),
  });

  const { scoredGoals, leadId } = useOnboardingGoalScoring(wizard.answers);

  useOnboardingWizardApiFlow({
    step: wizard.step,
    answers: wizard.answers,
    simulateLoadingError: harness.simulateLoadingError,
    onGenerateSuccess: wizard.completeLoading,
    onGenerateError: wizard.failLoading,
  });

  useEffect(() => {
    if (
      !harness.enabled ||
      harness.wizardStep !== "fitness-score-result" ||
      getOnboardingV3Session()?.fitnessScore
    ) {
      return;
    }

    const fixture = buildHarnessResultValues(
      harness.resultVariant ?? "million",
    );
    persistOnboardingV3Session({
      selectedGoalType: "million",
      goalId: "million",
      previewId: "harness-preview",
      resultValues: fixture ?? {
        goalType: "million",
        values: {},
      },
      fitnessScore: MOCK_ONBOARDING_FITNESS_SCORE,
      generateCompletedAt: Date.now(),
    });
  }, [harness.enabled, harness.resultVariant, harness.wizardStep]);

  const goalType = useMemo(
    () => mapGoalIdToGoalType(wizard.answers.goalId),
    [wizard.answers.goalId],
  );

  const loaderChecklist = useMemo(
    () => getLoaderChecklistForGoalType(goalType),
    [goalType],
  );

  const resultUI = useMemo(() => {
    const session = getOnboardingV3Session();
    if (session?.resultValues) {
      return mapResultValuesToResultUI(session.resultValues);
    }
    if (harness.enabled && harness.resultVariant && wizard.step === "result") {
      const fixture = buildHarnessResultValues(harness.resultVariant);
      if (fixture) {
        return mapResultValuesToResultUI(fixture);
      }
    }
    return mapResultValuesToResultUI(null);
  }, [harness.enabled, harness.resultVariant, wizard.step]);

  const paywallDeps = useMemo(
    () => ({
      isPC,
      isMember,
      chatApi,
      requestHeaders,
      openPlanResumeDraftGoalChat,
      onMissingDraftId: () => {
        log.warn("[onboarding] missing draftId for paywall handoff");
      },
    }),
    [chatApi, isMember, isPC, openPlanResumeDraftGoalChat, requestHeaders],
  );

  useEffect(() => {
    if (wizard.step !== "goal" || goalCardsTrackedRef.current) {
      return;
    }
    goalCardsTrackedRef.current = true;
    trackEvent("ob_goal_cards_shown", {
      cards: scoredGoals.map((g) => g.option.id),
      lead: leadId,
    });
  }, [leadId, scoredGoals, wizard.step]);

  const handleUnlockPcPlanOverview = useCallback(async () => {
    if (!pcPlanPreview?.draftId) {
      log.warn("[onboarding] plan preview unavailable for unlock");
      return;
    }

    navigateOnboardingPlanUnlockToBrief(router);
    setPcPlanPreview(null);

    trackEvent("ob_plan_view", {
      goal: getOnboardingV3Session()?.selectedGoalType,
    });

    try {
      await resolveOnboardingPlanUnlock(paywallDeps);
    } catch (error) {
      log.warn("[onboarding] paywall outcome failed", error);
    }
  }, [paywallDeps, pcPlanPreview, router]);

  const handleOpenPlanOverview = useCallback(async () => {
    const session = getOnboardingV3Session();
    if (!session?.previewId) {
      return;
    }

    setIsOpeningPlanOverview(true);
    try {
      let planPreview = session.planPreview;
      if (!planPreview?.draftId) {
        try {
          planPreview = await runOnboardingPlanPreviewRequest(
            chatApi,
            requestHeaders,
            session.previewId,
          );
          patchOnboardingV3Session({ planPreview });
        } catch (error) {
          log.warn("[onboarding] plan-preview before modal failed", error);
          trackEvent("ob_loading_error", {
            phase: "plan_preview_modal",
            code:
              error instanceof OnboardingV3ApiError ? error.code : "UNKNOWN",
          });
          return;
        }
      }

      if (isPC) {
        setPcPlanPreview(planPreview);
        return;
      }

      await openOnboardingPlanOverviewModal({
        isPC,
        planPreview,
        paywallDeps,
        onNavigateToBrief: () => {
          navigateOnboardingPlanUnlockToBrief(router);
        },
        onMissingPlanPreview: () => {
          log.warn("[onboarding] plan preview unavailable for unlock");
        },
      });
    } finally {
      setIsOpeningPlanOverview(false);
    }
  }, [chatApi, isPC, paywallDeps, requestHeaders, router]);

  useEffect(() => {
    if (!harness.enabled || harnessModalsOpenedRef.current) {
      return;
    }
    if (!harness.openPlanModal && !harness.openPaywall) {
      return;
    }

    harnessModalsOpenedRef.current = true;

    void callEdgenModalWhenRootReadyAsync(async () => {
      if (harness.openPlanModal) {
        await handleOpenPlanOverview();
      }
      if (harness.openPaywall) {
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, 500);
        });
        await openOnboardingPaywall(isPC);
      }
    });
  }, [
    handleOpenPlanOverview,
    harness.enabled,
    harness.openPaywall,
    harness.openPlanModal,
    isPC,
  ]);

  const { step } = wizard;

  if (pcPlanPreview) {
    return (
      <PlanOverviewModalContent
        isPC
        planPreview={pcPlanPreview}
        onBack={() => setPcPlanPreview(null)}
        onUnlock={() => {
          void handleUnlockPcPlanOverview();
        }}
      />
    );
  }

  if (step === "score-intro") {
    return <ScoreIntroStep onContinue={wizard.goNext} />;
  }

  if (step === "value-prop") {
    return <ValuePropStep onContinue={wizard.goNext} />;
  }

  if (step === "quiz/name") {
    return (
      <NameQuizStep
        value={wizard.answers.name}
        progressIndex={wizard.quizProgressIndex}
        progressTotal={wizard.quizProgressTotal}
        onBack={wizard.goBack}
        onContinue={(name) => {
          wizard.setAnswer("name", name);
          wizard.goNext();
        }}
      />
    );
  }

  const quizDef = getQuizDefinitionForStep(step);
  if (quizDef?.kind === "choice") {
    const answerKey = quizDef.answerKey;
    return (
      <ChoiceQuizStep
        definition={quizDef}
        value={String(wizard.answers[answerKey])}
        displayName={wizard.displayName}
        progressIndex={wizard.quizProgressIndex}
        progressTotal={wizard.quizProgressTotal}
        onBack={wizard.goBack}
        onChange={(value) => wizard.setAnswer(answerKey, value)}
        onContinue={(selectedValue) => {
          wizard.goNext({ ...wizard.answers, [answerKey]: selectedValue });
        }}
      />
    );
  }

  if (quizDef?.kind === "numeric") {
    const answerKey = quizDef.answerKey;
    return (
      <NumericQuizStep
        definition={quizDef}
        value={Number(wizard.answers[answerKey])}
        displayName={wizard.displayName}
        progressIndex={wizard.quizProgressIndex}
        progressTotal={wizard.quizProgressTotal}
        onBack={wizard.goBack}
        onChange={(value) => wizard.setAnswer(answerKey, value)}
        onContinue={wizard.goNext}
      />
    );
  }

  if (step === "goal") {
    return (
      <GoalSelectionStep
        scoredGoals={scoredGoals}
        leadGoalId={leadId}
        selectedId={wizard.answers.goalId}
        progressIndex={wizard.quizProgressIndex}
        progressTotal={wizard.quizProgressTotal}
        onBack={wizard.goBack}
        onSelect={(goalId, rank) => {
          wizard.setAnswer("goalId", goalId);
          trackEvent("ob_goal_selected", {
            goal: mapGoalIdToGoalType(goalId),
            rank,
          });
          wizard.goNext();
        }}
      />
    );
  }

  if (step === "loading") {
    return (
      <LoaderStep
        checklist={loaderChecklist}
        freezeAutoComplete={harness.freezeLoader}
      />
    );
  }

  if (step === "loading-error") {
    return <LoaderErrorStep onRetry={wizard.retryLoading} />;
  }

  if (step === "fitness-score-result") {
    const fitnessScore = getOnboardingV3Session()?.fitnessScore;
    if (!fitnessScore) {
      return (
        <ResultStep
          resultUI={resultUI}
          loading={isOpeningPlanOverview}
          onContinue={() => {
            void handleOpenPlanOverview();
          }}
        />
      );
    }

    return (
      <FitnessScoreResultStep
        fitnessScore={fitnessScore}
        onContinue={wizard.goNext}
      />
    );
  }

  if (step === "result") {
    return (
      <ResultStep
        resultUI={resultUI}
        loading={isOpeningPlanOverview}
        onContinue={() => {
          void handleOpenPlanOverview();
        }}
      />
    );
  }

  return null;
};

export const OnboardingWizardShell: FC = () => {
  const bootstrap = useOnboardingWizardBootstrap();

  if (!bootstrap.ready) {
    return (
      <div
        className="min-h-dvh bg-background_solid_neutral_secondary"
        data-testid="onboarding-wizard-bootstrap"
        aria-busy="true"
      />
    );
  }

  return (
    <OnboardingWizardShellContent
      key={bootstrap.resetKey}
      initialStep={bootstrap.initialStep}
    />
  );
};
